import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage, handlePlanLimitError } from '../lib/planLimit';
import {
  ArrowLeft, ArrowRight, Check, ExternalLink, Loader2,
  Sparkles, ListChecks, FileText, AlertCircle, BookOpen,
  ScanLine, SkipForward, Copy, TrendingUp, Target, Lightbulb,
  Brain, Download, Zap, GitCompare, History,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { TaxQuestion } from '../components/tax/TaxQuestion';
import { TaxImportItem, TaxUploadZone } from '../components/tax/TaxImports';
import { FirstDeclarationForm } from '../components/tax/FirstDeclarationForm';
import { ComparisonView } from '../components/tax/ComparisonView';
import { BilanView } from '../components/tax/BilanView';
import {
  defaultAnneeRevenus, emptyFirstDeclaration, firstDeclarationPayload, flowSteps,
  n1ImportOptional, pruneHiddenAnswers, requiredAnswered, splitProfilQuestions,
  statusMeta, visibleQuestions, yearsFor,
} from '../lib/taxFlow';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryMeta = {
  revenus: { label: 'Revenus', color: '#3b82f6' },
  reductions: { label: 'Réductions / Crédits', color: '#10b981' },
  deductions: { label: 'Déductions', color: '#8b5cf6' },
  verifications: { label: 'Vérifications', color: '#f59e0b' },
  options: { label: 'Options', color: '#a1a1a6' },
};

const priorityMeta = {
  P0: { label: 'Important', color: '#ef4444' },
  P1: { label: 'À vérifier', color: '#f59e0b' },
  P2: { label: 'Optionnel', color: '#a1a1a6' },
};

const fadeProps = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 } };

const StepHeader = ({ badge, icon, title, children }) => (
  <div className="text-center mb-10">
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-widest bg-[#81d8d0]/15 text-[#81d8d0] mb-4">
      {icon} {badge}
    </div>
    <h2 className="text-3xl sm:text-4xl font-light mb-3">{title}</h2>
    {children && <p className="opacity-60 max-w-xl mx-auto">{children}</p>}
  </div>
);

const ValidationNotice = ({ isDark }) => (
  <div className={`p-4 rounded-xl ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border mb-6`}>
    <div className="flex gap-2 text-sm">
      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
      <p className="opacity-80">
        <strong>Important :</strong> Skipya ne déclare pas à ta place. Tu fais toujours la vérification finale et tu valides toi-même sur impots.gouv.fr.
      </p>
    </div>
  </div>
);

export const TaxAssistantPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [stepKey, setStepKey] = useState('profil');
  const [questions, setQuestions] = useState([]);
  const [anneeRevenus, setAnneeRevenus] = useState(defaultAnneeRevenus());
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imports, setImports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [firstDecl, setFirstDecl] = useState(emptyFirstDeclaration());
  const [firstChecklist, setFirstChecklist] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [bilan, setBilan] = useState(null);
  const [stats, setStats] = useState(null);
  const [exportRows, setExportRows] = useState([]);
  const [guide, setGuide] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const years = yearsFor(anneeRevenus);
  const profil = answers.profil_declarant;
  const steps = flowSteps(profil);
  const stepIndex = Math.max(0, steps.findIndex((s) => s.key === stepKey));
  const goTo = (key) => setStepKey(key);
  const next = () => goTo(steps[Math.min(stepIndex + 1, steps.length - 1)].key);
  const prev = () => goTo(steps[Math.max(stepIndex - 1, 0)].key);

  const { profil: profilQuestions, others: otherQuestions } = splitProfilQuestions(questions);
  const visibleProfil = visibleQuestions(profilQuestions, answers);
  // Visibility needs the full list (a sub-question may depend on any question)
  const visibleAll = visibleQuestions(questions, answers);
  const visibleOthers = visibleAll.filter((q) => q.section !== 'profil');

  const n1Imports = imports.filter((i) => i.usage === 'reference_n1');
  const nImports = imports.filter((i) => i.usage !== 'reference_n1' && i.usage !== 'archive');

  useEffect(() => {
    axios.get(`${API}/api/tax-assistant/questions`)
      .then((r) => {
        setQuestions(r.data.questions);
        if (r.data.annee_revenus) setAnneeRevenus(r.data.annee_revenus);
      })
      .catch(() => toast.error('Impossible de charger le questionnaire'));
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/tax-assistant/imports`, auth)
      .then((r) => setImports(r.data.imports || []))
      .catch(() => {});
  }, [token, auth]);

  const loadImports = async () => {
    try {
      const r = await axios.get(`${API}/api/tax-assistant/imports`, auth);
      setImports(r.data.imports || []);
    } catch (_e) {}
  };

  const handleUpload = async (file, usage) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('annee_revenus', String(anneeRevenus));
    fd.append('usage', usage);
    try {
      const r = await axios.post(`${API}/api/tax-assistant/extract-doc`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      const data = r.data;
      if (!data.doc_type) {
        toast.warning("Document non reconnu — réessaie avec un avis d'imposition, un bulletin de paie ou un reçu fiscal.");
      } else if (usage === 'reference_n1' && data.usage !== 'reference_n1') {
        toast.warning(`${data.doc_type_label} reconnu, mais il porte sur les revenus ${data.annee_revenus_document || '?'} : on attend l'avis sur les revenus ${years.anneeRevenusN1}.`);
      } else {
        const y = data.annee_revenus_document ? ` (revenus ${data.annee_revenus_document})` : '';
        toast.success(`Document reconnu : ${data.doc_type_label}${y}`);
      }
      await loadImports();
      if (result && data.matched_rules && data.matched_rules.length > 0) {
        setResult((res) => ({
          ...res,
          items: res.items.map((i) => (data.matched_rules.includes(i.id) ? { ...i, status: 'done' } : i)),
        }));
      }
    } catch (e) {
      if (!handlePlanLimitError(e, navigate)) {
        toast.error(apiErrorMessage(e, "Erreur lors de l'analyse du document"));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const saveFirstDeclaration = async () => {
    setLoading(true);
    try {
      const r = await axios.put(
        `${API}/api/tax-assistant/dossier/premiere-declaration`,
        firstDeclarationPayload(firstDecl, anneeRevenus),
        auth,
      );
      setFirstChecklist(r.data.checklist || []);
      toast.success(`Infos enregistrées dans ton dossier ${anneeRevenus}`);
      next();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Impossible d'enregistrer tes infos"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stepKey !== 'premiere' || firstChecklist.length) return;
    axios.get(`${API}/api/tax-assistant/premiere-declaration/checklist`, { params: { annee_revenus: anneeRevenus } })
      .then((r) => setFirstChecklist(r.data.checklist || []))
      .catch(() => {});
  }, [stepKey, anneeRevenus, firstChecklist.length]);

  const handleDiagnose = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/tax-assistant/diagnose`,
        { answers: pruneHiddenAnswers(questions, answers), annee_revenus: anneeRevenus },
        auth,
      );
      setResult(r.data);
      await loadImports();
      goTo('documents');
    } catch (_e) {
      toast.error('Erreur lors du diagnostic');
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/tax-assistant/comparison`, { ...auth, params: { annee_revenus: anneeRevenus } });
      setComparison(r.data);
      goTo('comparaison');
    } catch (_e) {
      toast.error('Impossible de calculer la comparaison');
    } finally {
      setLoading(false);
    }
  };

  const loadBilan = async () => {
    setLoading(true);
    try {
      const params = { params: { annee_revenus: anneeRevenus } };
      const [b, s, e] = await Promise.all([
        axios.get(`${API}/api/tax-assistant/bilan`, { ...auth, ...params }),
        axios.get(`${API}/api/tax-assistant/summary-stats`, auth),
        axios.get(`${API}/api/tax-assistant/export`, auth),
      ]);
      setBilan(b.data);
      setStats(s.data);
      setExportRows(e.data.rows || []);
      goTo('bilan');
    } catch (_e) {
      toast.error('Impossible de charger le bilan');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (item) => {
    const newStatus = item.status === 'done' ? 'todo' : 'done';
    setResult((r) => ({ ...r, items: r.items.map((i) => (i.id === item.id ? { ...i, status: newStatus } : i)) }));
    try {
      await axios.patch(`${API}/api/tax-assistant/checklist/item`, { item_id: item.id, status: newStatus }, auth);
    } catch (_e) {
      toast.error("La mise à jour n'a pas été enregistrée");
    }
  };

  const copyCase = (caseStr, value) => {
    const text = value !== null && value !== undefined
      ? `Case ${caseStr} : ${typeof value === 'number' ? value.toLocaleString('fr-FR') : value} €`
      : `Case ${caseStr}`;
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  const loadAiSuggestions = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/api/tax-assistant/ai-suggestions`, {}, auth);
      setAiSuggestions(r.data);
      toast.success('Analyse IA terminée');
    } catch (e) {
      if (e?.response?.status === 402) toast.error('Limite IA atteinte — passe à un plan supérieur.');
      else toast.error("Impossible d'obtenir l'analyse IA — réessaie.");
    } finally {
      setAiLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await axios.get(`${API}/api/tax-assistant/export-pdf`, { ...auth, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `skipya-recap-fiscal-${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (_e) {
      toast.error('Téléchargement du PDF impossible');
    } finally {
      setPdfLoading(false);
    }
  };

  const loadGuide = async () => {
    try {
      const r = await axios.get(`${API}/api/tax-assistant/guide`, auth);
      setGuide(r.data.steps || []);
    } catch (_e) {
      toast.error('Guide indisponible');
    }
  };

  const profilReady = requiredAnswered(profilQuestions, answers);
  const questionsReady = requiredAnswered(questions, answers);
  const cardClass = `${isDark ? '' : 'text-[#1d1d1f] '}p-6 sm:p-8 mb-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`;
  const primary = 'bg-[#81d8d0] text-black hover:bg-[#6bc4bc]';
  const grouped = result ? result.items.reduce((acc, it) => ({ ...acc, [it.category]: [...(acc[it.category] || []), it] }), {}) : {};

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`} data-testid="tax-assistant-page">
      <div className={`sticky top-0 z-10 backdrop-blur-xl ${isDark ? 'bg-[#0F0F1A]/80 border-white/10' : 'bg-white/80 border-black/10'} border-b`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-70 transition" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" /> <span className="text-sm">Retour</span>
          </Link>
          <h1 className="text-lg font-medium">Assistant fiscal</h1>
          <span className="text-[11px] opacity-60 text-right" data-testid="campaign-years">
            Revenus {years.anneeRevenus}<br />déclarés en {years.anneeDeclaration}
          </span>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-4">
          <Progress value={(stepIndex / (steps.length - 1)) * 100} className="h-1.5 mb-2" />
          <div className="flex justify-between text-[10px] uppercase tracking-widest opacity-50 gap-1">
            {steps.map((s, i) => (
              <span key={s.key} className={`${i <= stepIndex ? 'text-[#81d8d0]' : ''} hidden sm:inline`}>
                {i + 1}. {s.label}
              </span>
            ))}
            <span className="sm:hidden text-[#81d8d0]">{stepIndex + 1}/{steps.length} · {steps[stepIndex].label}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <AnimatePresence mode="wait">
          {stepKey === 'profil' && (
            <motion.div key="profil" {...fadeProps} data-testid="step-profil">
              <StepHeader badge="Diagnostic en 2 minutes" icon={<Sparkles className="w-3 h-3" />} title="Prépare ta déclaration sereinement.">
                Tu déclares en {years.anneeDeclaration} tes revenus {years.anneeRevenus}. Skipya te dit quelles cases vérifier sur impots.gouv.fr — sans rien déclarer à ta place.
              </StepHeader>
              <Card className={cardClass}>
                <div className="space-y-6">
                  {visibleProfil.map((q) => (
                    <TaxQuestion key={q.id} q={q} value={answers[q.id]} onChange={(v) => handleAnswer(q.id, v)} isDark={isDark} nested={Boolean(q.showIf)} />
                  ))}
                </div>
              </Card>
              <div className="flex justify-end">
                <Button onClick={next} disabled={!profilReady} className={`${primary} px-6`} data-testid="btn-profil-next">
                  Continuer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {stepKey === 'n1' && (
            <motion.div key="n1" {...fadeProps} data-testid="step-n1">
              <StepHeader
                badge={n1ImportOptional(profil) ? 'Étape optionnelle' : 'Ta référence N−1'}
                icon={<History className="w-3 h-3" />}
                title={n1ImportOptional(profil) ? "L'avis de tes parents, si tu l'as" : 'Importe ton dernier avis d\'imposition'}
              >
                {n1ImportOptional(profil)
                  ? `L'an dernier, tes revenus étaient sur la déclaration de tes parents. Leur avis ${years.anneeRevenus} (revenus ${years.anneeRevenusN1}) peut servir de repère, mais tu peux t'en passer.`
                  : `Importe ton avis d'impôt ${years.anneeRevenus} sur les revenus ${years.anneeRevenusN1}. On s'en sert pour comparer avec cette année.`}
              </StepHeader>
              <div className={`p-4 rounded-xl mb-6 text-xs border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-black/[0.02] border-black/5'}`}>
                Ce document est une <strong>référence</strong> : Skipya ne recopie jamais ses montants dans ta déclaration {years.anneeRevenus}.
                Tu peux aussi importer ta déclaration 2042 de l'an dernier.
              </div>
              <TaxUploadZone
                id="tax-n1-upload"
                uploading={uploading}
                onFile={(f) => handleUpload(f, 'reference_n1')}
                isDark={isDark}
                title={`Avis d'impôt ${years.anneeRevenus} (revenus ${years.anneeRevenusN1})`}
              />
              {n1Imports.length > 0 && (
                <div className="space-y-2 mb-8" data-testid="n1-imports-list">
                  {n1Imports.map((imp) => <TaxImportItem key={imp.id} imp={imp} isDark={isDark} />)}
                </div>
              )}
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={prev}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
                <div className="flex gap-2">
                  {n1Imports.length === 0 && (
                    <Button variant="outline" onClick={next} data-testid="btn-skip-n1">
                      <SkipForward className="w-4 h-4 mr-2" /> {n1ImportOptional(profil) ? 'Passer' : "Je ne l'ai pas sous la main"}
                    </Button>
                  )}
                  {n1Imports.length > 0 && (
                    <Button onClick={next} className={primary} data-testid="btn-n1-next">
                      Continuer <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {stepKey === 'premiere' && (
            <motion.div key="premiere" {...fadeProps} data-testid="step-premiere">
              <StepHeader badge="Première déclaration" icon={<Sparkles className="w-3 h-3" />} title="On prépare ta première déclaration">
                Quelques infos pour ton dossier, puis la liste de ce qu'il te faut.
              </StepHeader>
              <FirstDeclarationForm form={firstDecl} onChange={setFirstDecl} checklist={firstChecklist} years={years} isDark={isDark} />
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={prev}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
                <Button onClick={saveFirstDeclaration} disabled={loading} className={primary} data-testid="btn-save-first-declaration">
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Enregistrer et continuer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {stepKey === 'questions' && (
            <motion.div key="questions" {...fadeProps} data-testid="step-welcome">
              <StepHeader badge={`Ta situation ${years.anneeRevenus}`} icon={<ListChecks className="w-3 h-3" />} title="Quelques questions rapides">
                Les sous-questions n'apparaissent que si elles te concernent.
              </StepHeader>
              <Card className={cardClass}>
                <div className="space-y-5">
                  {visibleOthers.map((q) => (
                    <TaxQuestion key={q.id} q={q} value={answers[q.id]} onChange={(v) => handleAnswer(q.id, v)} isDark={isDark} nested={Boolean(q.showIf)} />
                  ))}
                </div>
              </Card>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={prev}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
                <Button onClick={handleDiagnose} disabled={!questionsReady || loading} className={`${primary} px-6`} data-testid="btn-diagnose">
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Analyser ma situation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {stepKey === 'documents' && result && (
            <motion.div key="documents" {...fadeProps} data-testid="step-documents">
              <StepHeader badge="Étape optionnelle" icon={<ScanLine className="w-3 h-3" />} title={`Tes justificatifs ${years.anneeRevenus}`}>
                Bulletin de décembre, attestation fiscale, reçus de dons, attestation CESU ou PER de {years.anneeRevenus} : Skipya les lit et pré-coche ta checklist.
              </StepHeader>
              <TaxUploadZone id="tax-doc-upload" uploading={uploading} onFile={(f) => handleUpload(f, 'justificatif_n')} isDark={isDark} title="Glisse ton document ici" />
              {nImports.length > 0 && (
                <div className="space-y-2 mb-8" data-testid="imports-list">
                  {nImports.map((imp) => <TaxImportItem key={imp.id} imp={imp} isDark={isDark} />)}
                </div>
              )}

              <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-5 mb-6 border ${isDark ? 'bg-gradient-to-br from-[#81d8d0]/10 to-transparent border-[#81d8d0]/30' : 'bg-gradient-to-br from-[#81d8d0]/15 to-transparent border-[#81d8d0]/40'}`}>
                <div className="flex items-start gap-3">
                  <ListChecks className="w-6 h-6 text-[#81d8d0] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">{result.summary_message}</h3>
                    <div className="flex gap-4 text-xs opacity-70">
                      <span><strong className="text-red-400">{result.counts.p0}</strong> importants</span>
                      <span><strong className="text-amber-400">{result.counts.p1}</strong> à vérifier</span>
                      <span><strong className="opacity-50">{result.counts.p2}</strong> optionnels</span>
                    </div>
                  </div>
                </div>
              </Card>
              {Object.keys(grouped).map((cat) => {
                const meta = categoryMeta[cat] || { label: cat, color: '#a1a1a6' };
                return (
                  <div key={cat} className="mb-6" data-testid={`cat-${cat}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                      <h3 className="text-sm uppercase tracking-widest opacity-70">{meta.label}</h3>
                    </div>
                    <div className="space-y-2">
                      {grouped[cat].map((item) => (
                        <ChecklistItem key={item.id} item={item} onToggle={() => toggleItem(item)} isDark={isDark} />
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => goTo('questions')} data-testid="btn-back-to-diagnostic">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Modifier mes réponses
                </Button>
                <Button onClick={loadComparison} disabled={loading} className={primary} data-testid="btn-to-comparison">
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Comparer avec l'an dernier <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {stepKey === 'comparaison' && comparison && (
            <motion.div key="comparaison" {...fadeProps} data-testid="step-comparison">
              <StepHeader badge={`Revenus ${years.anneeRevenusN1} → ${years.anneeRevenus}`} icon={<GitCompare className="w-3 h-3" />} title="Ce qui change cette année">
                Identique, nouveau, à supprimer, à vérifier ou justificatif manquant : chaque point a son statut.
              </StepHeader>
              <ComparisonView comparison={comparison} isDark={isDark} />
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => goTo('documents')}><ArrowLeft className="w-4 h-4 mr-2" /> Documents</Button>
                <Button onClick={loadBilan} disabled={loading} className={primary} data-testid="btn-to-summary">
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Voir mon bilan <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {stepKey === 'bilan' && bilan && (
            <motion.div key="bilan" {...fadeProps} data-testid="step-summary">
              <h2 className="text-3xl sm:text-4xl font-light mb-2">Ton bilan {years.anneeDeclaration}</h2>
              <p className="opacity-60 mb-8 max-w-xl">Revenus {years.anneeRevenus}. Tout ce qu'il faut avoir en tête avant d'ouvrir impots.gouv.fr.</p>

              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="summary-headline-stats">
                  <BigStat icon={<TrendingUp className="w-4 h-4" />} label="Revenu détecté"
                    value={stats.has_real_income ? `${stats.rni_estimated.toLocaleString('fr-FR')} €` : '—'}
                    hint={stats.has_real_income ? `Justificatifs ${years.anneeRevenus}` : `Importe un justificatif ${years.anneeRevenus}`} isDark={isDark} />
                  <BigStat icon={<FileText className="w-4 h-4" />} label="Impôt estimé"
                    value={stats.has_real_income ? `${stats.ir_estimated.toLocaleString('fr-FR')} €` : '—'}
                    hint="Estimation indicative" isDark={isDark} accent="#FF6B6B" />
                  <BigStat icon={<Sparkles className="w-4 h-4" />} label="Économies détectées"
                    value={`${stats.savings_detected.toLocaleString('fr-FR')} €`} hint="Moyennes indicatives" isDark={isDark} accent="#10b981" />
                  <BigStat icon={<Target className="w-4 h-4" />} label="Optimisation"
                    value={`${stats.optimisation_score}%`} hint={`${stats.items_done}/${stats.items_total} préparés`} isDark={isDark} accent="#81d8d0" />
                </div>
              )}

              <BilanView bilan={bilan} isDark={isDark} />

              {bilan.premiere_declaration && (
                <Card className={cardClass} data-testid="bilan-premiere">
                  <h3 className="font-medium mb-2 text-sm">Première déclaration</h3>
                  <p className="text-xs opacity-70 mb-3">{bilan.premiere_declaration.online}</p>
                  <ul className="space-y-1.5 text-xs">
                    {bilan.premiere_declaration.checklist.map((c) => (
                      <li key={c.id}><strong>{c.title}</strong> <span className="opacity-70">— {c.detail}</span></li>
                    ))}
                  </ul>
                </Card>
              )}

              {stats && stats.forgotten && stats.forgotten.length > 0 && (
                <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-5 mb-6 ${isDark ? 'bg-amber-500/8 border-amber-500/25' : 'bg-amber-50 border-amber-200'} border`} data-testid="forgotten-alerts">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium mb-2 text-sm">Cases souvent oubliées</h3>
                      <ul className="space-y-1.5">
                        {stats.forgotten.map((f) => (
                          <li key={f.id} className="text-xs opacity-80 flex items-start gap-2"><span className="text-amber-500 mt-0.5">→</span><span>{f.message}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              )}

              {exportRows.length > 0 && (
                <Card className={cardClass} data-testid="export-cases">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <h3 className="font-medium text-base flex items-center gap-2"><Copy className="w-4 h-4 text-[#81d8d0]" /> Cases à traiter sur impots.gouv.fr</h3>
                      <p className="text-xs opacity-60 mt-0.5">Prérempli = à vérifier, pas à ressaisir. Aussi disponible dans l'extension Skipya.</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {exportRows.map((r) => <ExportRow key={r.rule_id} row={r} isDark={isDark} onCopy={copyCase} />)}
                  </div>
                </Card>
              )}

              <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-6 mb-6 border ${isDark ? 'bg-gradient-to-br from-[#81d8d0]/8 to-transparent border-[#81d8d0]/25' : 'bg-gradient-to-br from-[#81d8d0]/12 to-transparent border-[#81d8d0]/30'}`} data-testid="ai-suggestions-card">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-[#81d8d0]/20 flex items-center justify-center flex-shrink-0"><Brain className="w-5 h-5 text-[#81d8d0]" /></div>
                  <div className="flex-1">
                    <h3 className="font-medium mb-0.5 flex items-center gap-2">
                      Suggestions intelligentes
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#81d8d0]/20 text-[#81d8d0]">IA</span>
                    </h3>
                    <p className="text-xs opacity-60">Analyse contextuelle — repère les cases oubliées et les optimisations possibles.</p>
                  </div>
                </div>
                {!aiSuggestions && (
                  <Button onClick={loadAiSuggestions} disabled={aiLoading} className={primary} data-testid="btn-load-ai-suggestions">
                    {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Skipya analyse ta situation…</> : <><Zap className="w-4 h-4 mr-2" /> Lancer l'analyse IA</>}
                  </Button>
                )}
                {aiSuggestions && (
                  <div data-testid="ai-suggestions-result">
                    {aiSuggestions.global_advice && (
                      <div className={`p-3 mb-3 rounded-lg text-sm italic ${isDark ? 'bg-white/[0.04]' : 'bg-white/60'}`}>« {aiSuggestions.global_advice} »</div>
                    )}
                    {aiSuggestions.suggestions && aiSuggestions.suggestions.length > 0 ? (
                      <div className="space-y-2">{aiSuggestions.suggestions.map((s, i) => <AiSuggestionRow key={i} s={s} isDark={isDark} />)}</div>
                    ) : (
                      <p className="text-xs opacity-60">Aucune suggestion supplémentaire.</p>
                    )}
                  </div>
                )}
              </Card>

              {guide.length > 0 && (
                <div className="space-y-3 mb-6" data-testid="guide-steps">
                  {guide.map((s) => (
                    <Card key={s.n} className={`${isDark ? '' : 'text-[#1d1d1f] '}p-5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`} data-testid={`guide-step-${s.n}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-9 h-9 rounded-full bg-[#81d8d0]/15 flex items-center justify-center font-medium text-[#81d8d0] flex-shrink-0">{s.n}</div>
                        <div><div className="font-medium mb-1">{s.title}</div><p className="text-sm opacity-70">{s.detail}</p></div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              <ValidationNotice isDark={isDark} />

              <div className="flex flex-wrap gap-3">
                <Button onClick={() => goTo('comparaison')} variant="ghost" data-testid="btn-back-checklist">Revoir la comparaison</Button>
                {guide.length === 0 && (
                  <Button onClick={loadGuide} variant="outline" data-testid="btn-load-guide"><BookOpen className="w-4 h-4 mr-2" /> Guide pas-à-pas</Button>
                )}
                <Button onClick={downloadPdf} variant="outline" disabled={pdfLoading} data-testid="btn-download-pdf">
                  {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Télécharger le PDF
                </Button>
                <a href="https://www.impots.gouv.fr/accueil" target="_blank" rel="noopener noreferrer" className="sm:ml-auto">
                  <Button className={primary} data-testid="btn-open-impots">Ouvrir impots.gouv.fr <ExternalLink className="w-4 h-4 ml-2" /></Button>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ============= Components =============

const ChecklistItem = ({ item, onToggle, isDark }) => {
  const p = priorityMeta[item.priority] || priorityMeta.P2;
  const done = item.status === 'done';
  return (
    <button
      onClick={onToggle}
      data-testid={`item-${item.id}`}
      className={`w-full text-left p-4 rounded-xl transition border ${
        done
          ? isDark ? 'bg-[#81d8d0]/10 border-[#81d8d0]/30' : 'bg-[#81d8d0]/15 border-[#81d8d0]/40'
          : isDark ? 'bg-white/5 border-white/10 hover:bg-white/8' : 'bg-white border-black/5 hover:bg-black/[0.02]'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded-full mt-0.5 border-2 flex items-center justify-center flex-shrink-0 ${done ? 'bg-[#81d8d0] border-[#81d8d0]' : isDark ? 'border-white/30' : 'border-black/20'}`}>
          {done && <Check className="w-3 h-3 text-black" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className={`font-medium ${done ? 'line-through opacity-60' : ''}`}>{item.title}</span>
            <span className="text-[10px] uppercase tracking-widest" style={{ color: p.color }}>{p.label}</span>
            {item.prefilled && <span className="text-[10px] uppercase tracking-widest opacity-60">Prérempli</span>}
          </div>
          <div className="text-xs opacity-70 mb-2">{item.explanation}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] opacity-60">
            <span><strong>Case :</strong> {item.case}</span>
            <span><strong>Document :</strong> {item.doc}</span>
          </div>
        </div>
      </div>
    </button>
  );
};

const BigStat = ({ icon, label, value, hint, accent, isDark }) => (
  <div className={`p-4 rounded-2xl ${isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-black/5'} border`} data-testid={`big-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-1.5 mb-2 opacity-60" style={accent ? { color: accent } : {}}>
      {icon}
      <span className="text-[10px] uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-light" style={accent ? { color: accent } : {}}>{value}</div>
    <div className="text-[11px] opacity-50 mt-1">{hint}</div>
  </div>
);

const AI_CATEGORY_META = {
  optimisation: { color: '#10b981', label: 'Optimisation' },
  attention: { color: '#f59e0b', label: 'Attention' },
  opportunite: { color: '#3b82f6', label: 'Opportunité' },
  verification: { color: '#a1a1a6', label: 'Vérification' },
};

const AiSuggestionRow = ({ s, isDark }) => {
  const meta = AI_CATEGORY_META[s.category] || AI_CATEGORY_META.verification;
  return (
    <div data-testid={`ai-suggestion-${s.category || 'unknown'}`} className={`p-3 rounded-lg border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-black/5'}`}>
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <span className="font-medium text-sm">{s.title}</span>
        <span className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
        {s.case && <code className="text-[10px] font-mono opacity-60 ml-auto">case {s.case}</code>}
      </div>
      <p className="text-xs opacity-80 leading-relaxed">{s.message}</p>
      {s.potential_eur && <p className="text-[11px] text-[#81d8d0] mt-1">≈ {s.potential_eur}</p>}
    </div>
  );
};

const ACTION_COLORS = { 'vérifier': '#f59e0b', ajouter: '#3b82f6', supprimer: '#ef4444' };

const ExportRow = ({ row, isDark, onCopy }) => {
  const hasValue = row.value !== null && row.value !== undefined;
  const status = row.comparison_status ? statusMeta(row.comparison_status) : null;
  return (
    <div data-testid={`export-row-${row.rule_id}`} className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-white/[0.03] hover:bg-white/[0.06]' : 'bg-black/[0.02] hover:bg-black/[0.04]'} transition`}>
      <div className="flex-shrink-0 w-20 sm:w-28">
        <code className={`text-[11px] sm:text-xs font-mono ${hasValue ? 'text-[#81d8d0]' : 'opacity-50'}`}>
          {row.case.length > 28 ? `${row.case.slice(0, 26)}…` : row.case}
        </code>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">{row.title}</div>
        <div className="flex flex-wrap items-baseline gap-2 text-[10px] uppercase tracking-widest mt-0.5">
          {row.action && <span style={{ color: ACTION_COLORS[row.action] }}>{row.action}</span>}
          {row.prefilled && <span className="opacity-60">prérempli</span>}
          {status && <span style={{ color: status.color }}>{status.label}</span>}
        </div>
        {hasValue && (
          <div className="text-xs opacity-70 flex items-baseline gap-2">
            <strong className="text-[#81d8d0]">{typeof row.value === 'number' ? row.value.toLocaleString('fr-FR') : row.value} €</strong>
            {row.source && <span className="text-[10px] opacity-50">depuis {row.source}</span>}
          </div>
        )}
      </div>
      <button
        onClick={() => onCopy(row.case, row.value)}
        data-testid={`btn-copy-${row.rule_id}`}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-black/5 hover:bg-black/10'}`}
      >
        <Copy className="w-3 h-3" /> Copier
      </button>
    </div>
  );
};

export default TaxAssistantPage;
